import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { db } from '../../services/firebase';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  doc,
  updateDoc,
  Timestamp,
  getDoc,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Avatar,
  Divider,
  Alert
} from '@mui/material';
import { User } from '../../types';
import MessageButton from '../Messages/MessageButton';
import ProfileCard from '../Profile/ProfileCard';

interface MatchRequest {
  id: string;
  senderId: string;
  message: string;
  status: 'pending' | 'accepted' | 'rejected';
  timestamp: Timestamp;
  senderProfile?: {
    displayName: string;
    profilePicture?: string;
  };
}

const MatchRequests: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<MatchRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [senderProfiles, setSenderProfiles] = useState<User[]>([]);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'matchRequests'),
      where('receiverId', '==', user.uid),
      where('status', '==', 'pending')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const requestsData: MatchRequest[] = [];
      const profiles: User[] = [];
      
      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data() as MatchRequest;
        const senderRef = doc(db, 'users', data.senderId);
        const senderDoc = await getDoc(senderRef);
        const senderData = senderDoc.data() as User;
        
        requestsData.push({
          ...data,
          id: docSnapshot.id,
          senderProfile: {
            displayName: senderData?.displayName || 'Unknown User',
            profilePicture: senderData?.profilePicture
          }
        });
        profiles.push({ ...senderData, uid: data.senderId });
      }
      
      setRequests(requestsData);
      setSenderProfiles(profiles);
    }, (err) => {
      setError('Failed to load match requests');
      console.error(err);
    });

    return () => unsubscribe();
  }, [user]);

  const handleRequest = async (requestId: string, accept: boolean) => {
    try {
      const requestRef = doc(db, 'matchRequests', requestId);
      await updateDoc(requestRef, {
        status: accept ? 'accepted' : 'rejected',
        respondedAt: serverTimestamp()
      });

      if (accept) {
        // Update both users' matches arrays
        const request = requests.find(r => r.id === requestId);
        if (request && user) {
          const userRef = doc(db, 'users', user.uid);
          const senderRef = doc(db, 'users', request.senderId);

          await Promise.all([
            updateDoc(userRef, {
              matches: arrayUnion(request.senderId)
            }),
            updateDoc(senderRef, {
              matches: arrayUnion(user.uid)
            })
          ]);
        }
      }
    } catch (err) {
      setError('Failed to process request');
      console.error(err);
    }
  };

  if (!user) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Typography 
        variant="h4" 
        gutterBottom 
        sx={{ 
          textAlign: { xs: 'center', md: 'left' }
        }}
      >
        Match Requests
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2}>
        {requests.length === 0 ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            gap: 2,
            py: 4 
          }}>
            <Typography color="black" variant="h6">
              No pending match requests
            </Typography>
            <Typography color="black" variant="h6" sx={{ textAlign: 'center' }}>
             Learn to communicate with your matches in their native language on Duolingo!
            </Typography>
            <Box
              component="img"
              src="/norequest.gif"
              alt="No requests illustration"
              sx={{
                width: '300',
                height: '200px',
                opacity: 1
              }}
            />
          </Box>
        ) : (
          <>
            <Typography variant="h6" sx={{ mb: 2 }}>
              You have {requests.length} pending request{requests.length > 1 ? 's' : ''}
            </Typography>
            {requests.map((request, index) => {
              const senderProfile = senderProfiles[index];
              return (
                <Box key={request.id} sx={{ mb: 4 }}>
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Match Request Message:
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2 }}>
                        {request.message}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={() => handleRequest(request.id, true)}
                        >
                          <Typography variant="body1" sx={{ color: 'white' }}>
                            Accept
                          </Typography>
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRequest(request.id, false)}
                        >
                          Decline
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                  
                  {senderProfile && (
                    <ProfileCard
                      profile={senderProfile}
                      onMatchRequest={() => {}}
                      isMatchRequestPage={true}
                    />
                  )}
                </Box>
              );
            })}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default MatchRequests; 